// theme.js
import { Platform } from 'react-native';

export const SPACING = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 };

export const RADIUS = {
  sm: 10,
  md: 14,
  lg: 18,
  pill: 999,
};

export const SIZES = {
  headerHeight: 56,

  headerBtn: 40,
  headerBtnIcon: 18,

  searchHeight: 44,
  toggleHeight: 42,
  toggleSwitchW: 58,
  toggleSwitchH: 26,
  toggleThumb: 24,
  toggleThumbTravel: 26,

  gridGap: 12,
  cardMax: 184,
  cardMin: 140,

  badge: 34,

  emptyPiece: 140,
};

export const COLORS = {
  // Base background (closer to the mock)
  bgTop: '#0B1424',
  bgBottom: '#070B14',

  background: '#0B1424',
  text: '#FFFFFF',
  textMuted: '#94A3B8',

  // Glass / borders
  glass: 'rgba(255,255,255,0.06)',
  glass2: 'rgba(255,255,255,0.08)',
  border: 'rgba(255,255,255,0.10)',
  borderSoft: 'rgba(255,255,255,0.06)',

  // Toggle (wood -> gold)
  woodDark: '#7A5236',
  woodMid: '#A57A54',
  gold: '#D5A02A',

  // Switch
  switchTrack: 'rgba(0,0,0,0.38)',
  switchThumb: '#E9E1D8',

  // Card tints (native overlays)
  whiteTintA: 'rgba(176,138,93,0.34)',
  blackTintA: 'rgba(213,160,42,0.30)',
  tintB: 'rgba(0,0,0,0.26)',
  spot: 'rgba(255,255,255,0.14)',

  // Header buttons
  headerBtnBg: 'rgba(255,255,255,0.08)',
  headerBtnBorder: 'rgba(255,255,255,0.10)',

  // Trainer UI
  trainerPanelBg: 'rgba(18, 24, 34, 0.82)',
  trainerPanelBorder: 'rgba(213, 160, 42, 0.70)',

  trainerMoveStripBg: 'rgba(255,255,255,0.06)',
  trainerMoveActiveBg: 'rgba(213, 160, 42, 0.88)',

  trainerHintSolid: '#D5A02A',
};

export const TYPO = {
  logoTop: {
    color: COLORS.text,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.4,
    lineHeight: 12,
  },
  logoBottom: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.35,
    lineHeight: 11,
  },
  headerIcon: {
    color: COLORS.text,
    fontSize: SIZES.headerBtnIcon,
    fontWeight: '800',
  },
};

export const GRADIENTS = {
  // Global background (web): multi-layer to match the mock depth
  appBgWeb:
    `radial-gradient(circle at 16% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 44%),` +
    `radial-gradient(circle at 86% 78%, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0) 48%),` +
    `linear-gradient(180deg, ${COLORS.bgTop} 0%, ${COLORS.bgBottom} 100%)`,

  // subtle “texture” (web)
  appNoiseWeb:
    `repeating-linear-gradient(135deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, rgba(0,0,0,0) 8px, rgba(0,0,0,0) 16px)`,

  // Toggle pill (web): wood -> gold
  togglePillWeb: `linear-gradient(90deg, ${COLORS.woodDark} 0%, ${COLORS.woodMid} 54%, ${COLORS.gold} 100%)`,

  // Cards (web): layered gradients for the “real” mock look
  cardWhiteWeb:
    `linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(0,0,0,0.30) 100%),` +
    `radial-gradient(circle at 18% 22%, rgba(176,138,93,0.60) 0%, rgba(176,138,93,0) 62%),` +
    `radial-gradient(circle at 82% 24%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 58%)`,

  cardBlackWeb:
    `linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(0,0,0,0.30) 100%),` +
    `radial-gradient(circle at 18% 22%, rgba(213,160,42,0.54) 0%, rgba(213,160,42,0) 62%),` +
    `radial-gradient(circle at 82% 24%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 58%)`,

  // Trainer hint button (web)
  trainerHintBtnWeb:
    `linear-gradient(180deg, rgba(255,255,255,0.26) 0%, rgba(0,0,0,0.18) 100%),` +
    `linear-gradient(90deg, rgba(213,160,42,0.95) 0%, rgba(245,158,11,0.92) 55%, rgba(213,160,42,0.92) 100%)`,
};

export const SHADOWS = {
  headerBtn: Platform.select({
    web: { boxShadow: '0px 8px 18px rgba(0,0,0,0.40)' },
    default: {
      shadowColor: '#000',
      shadowOpacity: 0.35,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 6 },
      elevation: 6,
    },
  }),
  card: Platform.select({
    web: { boxShadow: '0px 10px 20px rgba(0,0,0,0.38)' },
    default: {
      shadowColor: '#000',
      shadowOpacity: 0.35,
      shadowRadius: 14,
      shadowOffset: { width: 0, height: 10 },
      elevation: 7,
    },
  }),

  // Trainer panel shadow (subtle but present)
  trainerPanel: Platform.select({
    web: { boxShadow: '0px 18px 34px rgba(0,0,0,0.45)' },
    default: {
      shadowColor: '#000',
      shadowOpacity: 0.42,
      shadowRadius: 18,
      shadowOffset: { width: 0, height: 12 },
      elevation: 10,
    },
  }),

  trainerStrip: Platform.select({
    web: { boxShadow: '0px 10px 18px rgba(0,0,0,0.35)' },
    default: {
      shadowColor: '#000',
      shadowOpacity: 0.30,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 8 },
      elevation: 7,
    },
  }),
};
